import { IComputedValue, IObservableValue } from "mobx";
import {
  Canvas,
  EvaluateExpressionType,
  PaneInput,
  PaneOutput,
  PaneType,
  Rect,
} from "../../shared/Types";
import { MobxAtom } from "./atom";
export * from "../../shared/Types";

export type CanvasData =
  | [type: "not-sync", canvas: Canvas]
  | [type: "sync", canvasId: string]
  | [type: "sync", canvasId: undefined, inviteCode: string];

export type LoadState<T> =
  | [type: "error", message?: string]
  | [type: "loading", id?: string]
  | [type: "value", value: T];

export type CanvasSelection =
  | [type: "draft", canvasId: string]
  | [type: "file", canvasId: string, fileName: string | undefined]
  | [type: "example", canvasId: string]
  | [type: "tutorial", tutorialId: string]
  | [type: "user", username: string, canvasId: string]
  | [type: "invite", inviteCode: string];

export type SyncCanvasId =
  | [type: "canvas", canvasId: string]
  | [type: "invite", inviteCode: string];

export type SetCanvasOptions = {
  isSafeMode?: boolean;
  usedDefaultLayout?: boolean;
  initialViewport?: [x: number, y: number, zoom: number];
  initialSearchParamEntries?: Record<string, string>;
};

export enum PaneViewMode {
  Normal,
  Hidden,
  Collapsed,
}

export type ActivePaneState = {
  rect: IComputedValue<Rect>;
  zIndex: IComputedValue<number>;
  viewMode: IComputedValue<PaneViewMode>;
} & (
  | {
      type: PaneType.Evaluate;
      functionGet: { get(): LoadState<Function> };
      outputBoxes: [IObservableValue<PaneOutput>];
      editorHeight: IComputedValue<number | undefined>;
      isTryCatchDisabledBox: IObservableValue<boolean>;
      isLogInspectorEnabledBox: IObservableValue<boolean>;
      consoleLogValuesBox: IObservableValue<
        { runnerId: number; values: any[] } | undefined
      >;
      inspectorLogValuesBox: IObservableValue<
        [from: number, to: number, name: string, values: any[]][] | undefined
      >;
      outputDepsBox: IObservableValue<
        [expression: string, input: any, expressionType: EvaluateExpressionType]
      >;
    }
  | {
      type: PaneType.EvaluateGlobal;
      outputBoxes: [IObservableValue<PaneOutput>];
      editorHeight: IComputedValue<number | undefined>;
    }
  | {
      type: PaneType.EvaluateTemplate;
      outputBoxes: [IObservableValue<PaneOutput>];
      editorHeight: IComputedValue<number | undefined>;
      paneInputs: MobxAtom<Omit<PaneInput, "source">[] | undefined>;
    }
  | {
      type: PaneType.Library;
      outputBoxes: [IObservableValue<PaneOutput>];
    }
  | {
      type: PaneType.LLM;
      outputBoxes: [IObservableValue<PaneOutput>];
      paneInputsComputed: IComputedValue<PaneInput[]>;
      editorHeight: IComputedValue<number | undefined>;
    }
  | {
      type: PaneType.State;
      outputBoxes: [
        IObservableValue<PaneOutput>,
        IObservableValue<PaneOutput>,
        IObservableValue<PaneOutput>
      ];
      editorHeight: IComputedValue<number | undefined>;
    }
  | {
      type: PaneType.InputText;
      outputBoxes: [IObservableValue<PaneOutput>];
    }
  | {
      type: PaneType.Import;
      outputBoxes: [IObservableValue<PaneOutput>];
    }
  | {
      type: PaneType.EnvironmentVariable;
      outputBoxes: [IObservableValue<PaneOutput>];
    }
  | {
      type: PaneType.Function;
      outputBoxes: [IObservableValue<PaneOutput>];
    }
);

export type EnvironmentVariableValue =
  | ["loading"]
  | ["unauthorized"]
  | ["missing"]
  | ["value", string];

export type SandboxLayoutOverridePane = {
  rect: Rect;
  z: number;
  collapsed: boolean;
  editorHeight?: number | undefined;
};

export enum InputMode {
  Normal,
  PickFocusPane,
  Panning,
  TransformPane,
  ExposeZoom,
}
